import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { useUpdateEstimate } from '@/hooks/useEstimates';
import type { EstimateWithRelations } from '@/types/estimate';

interface EditEstimateDetailsDialogProps {
  estimate: EstimateWithRelations | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Edit the customer block printed on a quote (name, phone, email, billing
 * address). A customer may ask for the quote in their company name or with no
 * address; the save stamps bill_to_edited_at so contact sync from the lead or
 * customer card never overwrites it (migration 20260916110000).
 */
export function EditEstimateDetailsDialog({ estimate, open, onOpenChange }: EditEstimateDetailsDialogProps) {
  const updateEstimate = useUpdateEstimate();
  const { toast } = useToast();

  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [address, setAddress] = useState('');

  useEffect(() => {
    if (!estimate || !open) return;
    setName(estimate.customer_name || '');
    setPhone(estimate.customer_phone || '');
    setEmail(estimate.sent_to_email || '');
    setAddress(estimate.customer_address || '');
  }, [estimate, open]);

  const handleSave = async () => {
    if (!estimate || !name.trim()) return;
    const billToChanged =
      name.trim() !== (estimate.customer_name || '') ||
      phone.trim() !== (estimate.customer_phone || '') ||
      address.trim() !== (estimate.customer_address || '');
    try {
      await updateEstimate.mutateAsync({
        id: estimate.id,
        customer_name: name.trim(),
        customer_phone: phone.trim(),
        sent_to_email: email.trim(),
        customer_address: address.trim(),
        ...(billToChanged ? { bill_to_edited_at: new Date().toISOString() } : {}),
      });
      toast({ title: 'Quote details updated' });
      onOpenChange(false);
    } catch (e) {
      toast({ title: 'Could not save', description: e instanceof Error ? e.message : 'Unknown error', variant: 'destructive' });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Edit customer details{estimate?.estimate_number ? ` — ${estimate.estimate_number}` : ''}</DialogTitle>
          <DialogDescription>
            These are the details printed on the quote. Leave the address empty to leave it off the document.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="est-customer-name">Name *</Label>
            <Input id="est-customer-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Customer or company name" />
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="est-customer-phone">Phone</Label>
              <Input id="est-customer-phone" value={phone} onChange={(e) => setPhone(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="est-customer-email">Email (quote is sent here)</Label>
              <Input id="est-customer-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="est-customer-address">Billing address</Label>
            <Input id="est-customer-address" value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Leave empty to omit" />
          </div>
        </div>

        <div className="flex justify-end gap-3 border-t pt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSave} disabled={!name.trim() || updateEstimate.isPending}>
            {updateEstimate.isPending ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
